import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { uploadUserProfilePhoto, getUserProfile } from '../services/questionsService';
import { markNotificationAsRead, markAllNotificationsAsRead } from '../services/notificationService';
import { Link, useNavigate } from 'react-router-dom';
import ProfileRing from './ProfileRing';
import { getUserActiveRing } from '../services/profileRingService';

function UserMenu({ onLoginClick }) {
  const { currentUser, signOut } = useAuth();
  const { notifications, unreadCount } = useNotifications();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [userPhoto, setUserPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [userRingId, setUserRingId] = useState(null);
  const dropdownRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Get user initials or display name
  const getUserInitials = () => {
    if (!currentUser || currentUser.isAnonymous) return 'G';
    
    if (currentUser.displayName) {
      return currentUser.displayName
        .split(' ')
        .map(name => name ? name[0] : '')
        .join('')
        .toUpperCase()
        .substring(0, 2);
    }
    
    if (currentUser.email) {
      return 'U';
    }
    
    return 'U';
  };

  // Fetch user's profile including photo
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (currentUser && !currentUser.isAnonymous) {
        try {
          const profile = await getUserProfile(currentUser.uid);
          if (profile && profile.photoURL) {
            setUserPhoto(profile.photoURL);
          } else {
            setUserPhoto(currentUser.photoURL);
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
          setUserPhoto(currentUser.photoURL);
        }
      }
    };
    
    fetchUserProfile();
  }, [currentUser]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Load user's active ring
  useEffect(() => {
    const loadUserRing = async () => {
      if (currentUser?.uid) {
        try {
          const ringId = await getUserActiveRing(currentUser.uid);
          setUserRingId(ringId);
        } catch (error) {
          console.error('Error loading user ring:', error);
        }
      }
    };

    loadUserRing();
  }, [currentUser?.uid]);
  
  // Handle profile photo upload
  const handleProfilePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !currentUser) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file (JPG, PNG, etc.)');
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('Please select an image smaller than 2MB');
      return;
    }
    
    setUploading(true);
    try {
      const downloadURL = await uploadUserProfilePhoto(currentUser.uid, file);
      setUserPhoto(downloadURL);
      
      // Close dropdown after successful upload
      setDropdownOpen(false);
    } catch (error) {
      console.error('Error uploading profile photo:', error);
      alert('Failed to upload profile photo. Please try again.');
    } finally {
      setUploading(false);
    }
  };
  
  // Trigger file input click
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    try {
      // Mark as read if unread
      if (!notification.read) {
        await markNotificationAsRead(notification.id);
      }
      
      // Navigate based on notification type
      if (notification.type === 'mention' || notification.type === 'clap' || notification.type === 'tomato') {
        if (notification.target) {
          // Navigate to the specific content (could be a question, comment, etc.)
          navigate(`/`); // For now, navigate to home - can be enhanced later
        }
      } else if (notification.type === 'follow') {
        if (notification.fromUser) {
          navigate(`/profile/${notification.fromUser}`);
        }
      }
      
      setDropdownOpen(false);
    } catch (error) {
      console.error('Error handling notification click:', error);
    }
  };

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead(currentUser.uid);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Toggle notifications view
  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
  };

  // Format notification time
  const formatNotificationTime = (timestamp) => {
    const now = Date.now();
    const diff = now - timestamp;
    
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  const handleLogout = async () => {
    try {
      await signOut();
      setDropdownOpen(false);
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };
  
  // If not logged in or anonymous user
  if (!currentUser || currentUser.isAnonymous) {
    return (
      <div className="user-menu">
        <button className="login-button" onClick={onLoginClick}>
          Log In
        </button>
      </div>
    );
  }
  
  // For logged in users
  return (
    <div className="user-menu" ref={dropdownRef}>
      <div 
        className="user-avatar" 
        onClick={() => setDropdownOpen(!dropdownOpen)}
        style={{ cursor: 'pointer' }}
      >
        <ProfileRing ringId={userRingId} size="small">
          {userPhoto ? (
            <img src={userPhoto} alt="User" />
          ) : (
            getUserInitials()
          )}
        </ProfileRing>
        {unreadCount > 0 && (
          <div className="notification-dot"></div>
        )}
      </div>
      
      {dropdownOpen && (
        <div className="user-dropdown">
          {/* User Info */}
          <div className="dropdown-item user-info">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
              <path d="M12 5.9c1.16 0 2.1.94 2.1 2.1s-.94 2.1-2.1 2.1S9.9 9.16 9.9 8s.94-2.1 2.1-2.1m0 9c2.97 0 6.1 1.46 6.1 2.1v1.1H5.9V17c0-.64 3.13-2.1 6.1-2.1M12 4C9.79 4 8 5.79 8 8s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 9c-2.67 0-8 1.34-8 4v3h16v-3c0-2.66-5.33-4-8-4z"/>
            </svg>
            <span>
              {currentUser.displayName || 'User'}
            </span>
          </div>
          
          <div className="dropdown-divider"></div>
          
          {/* Notifications Section */}
          <div className="notifications-section">
            <div className="notification-header">
              <div 
                className="dropdown-item notifications-toggle"
                onClick={toggleNotifications}
                style={{ cursor: 'pointer' }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                  <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
                </svg>
                <span>
                  Notifications {unreadCount > 0 && `(${unreadCount})`}
                </span>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 24 24" 
                  fill="currentColor" 
                  width="16" 
                  height="16"
                  className={`chevron ${showNotifications ? 'rotated' : ''}`}
                >
                  <path d="M7.41 8.84L12 13.42l4.59-4.58L18 10.25l-6 6-6-6z"/>
                </svg>
              </div>
              
              {showNotifications && unreadCount > 0 && (
                <button 
                  className="mark-all-read-btn"
                  onClick={handleMarkAllAsRead}
                >
                  Mark all read
                </button>
              )}
            </div>
            
            {showNotifications && (
              <div className="notifications-list">
                {notifications && notifications.length > 0 ? (
                  notifications.slice(0, 5).map((notification) => (
                    <div 
                      key={notification.id}
                      className={`notification-item ${!notification.read ? 'unread' : ''}`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="notification-content">
                        <div className="notification-message">
                          {notification.message}
                        </div>
                        <div className="notification-time">
                          {formatNotificationTime(notification.timestamp)}
                        </div>
                      </div>
                      {!notification.read && <div className="unread-indicator"></div>}
                    </div>
                  ))
                ) : (
                  <div className="no-notifications">
                    <span>No notifications yet</span>
                  </div>
                )}
                
                {notifications && notifications.length > 5 && (
                  <div className="view-all-notifications">
                    <span>View all notifications</span>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="dropdown-divider"></div>
          
          {/* Profile Link */}
          <Link to={`/profile/${currentUser.uid}`} className="dropdown-item">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
            </svg>
            <span>
              View Profile
            </span>
          </Link>
          
          {/* Profile Photo Upload Option */}
          <div 
            className="dropdown-item"
            onClick={triggerFileInput}
            style={{ cursor: 'pointer' }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
              <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
            </svg>
            <span>{uploading ? 'Uploading...' : 'Change Profile Photo'}</span>
            <input 
              type="file" 
              ref={fileInputRef}
              onChange={handleProfilePhotoUpload}
              accept="image/*"
              style={{ display: 'none' }}
            />
          </div>
          
          <div className="dropdown-divider"></div>
          
          <div 
            className="dropdown-item"
            onClick={handleLogout}
            style={{ cursor: 'pointer' }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
              <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
            </svg>
            <span>Log Out</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserMenu;